
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {TradingCentralTimeFrame} from '../core/TradingCentralTimeFrame';
import {FieldModelTradingCentralTimeFrame} from '../core/TradingCentralTimeFrame';
import {TradingCentralLanguage} from '../core/TradingCentralLanguage';
import {FieldModelTradingCentralLanguage} from '../core/TradingCentralLanguage';

/**
 * TradingCentralArticlesQuery struct
 */
class TradingCentralArticlesQuery {
  /**
   * Initialize struct
   * @param {!TradingCentralTimeFrame=} timeframe
   * @param {!number=} limit
   * @param {!TradingCentralLanguage=} language
   * @constructor
   */
  constructor (timeframe = new TradingCentralTimeFrame(TradingCentralTimeFrame.DAY_7), limit = 100, language = new TradingCentralLanguage(TradingCentralLanguage.EN)) {
    this.Timeframe = timeframe
    this.Limit = limit
    this.Language = language
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TradingCentralArticlesQuery}
   * @param {!TradingCentralArticlesQuery} other Other struct
   * @returns {!TradingCentralArticlesQuery} This struct
   */
  copy (other) {
    if (other.Timeframe != null) {
      this.Timeframe = TradingCentralTimeFrame.fromObject(other.Timeframe)
    } else {
      this.Timeframe = null
    }
    if (other.Limit != null) {
      this.Limit = other.Limit
    } else {
      this.Limit = null
    }
    if (other.Language != null) {
      this.Language = TradingCentralLanguage.fromObject(other.Language)
    } else {
      this.Language = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TradingCentralArticlesQuery}
   * @returns {!TradingCentralArticlesQuery} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TradingCentralArticlesQueryModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TradingCentralArticlesQueryModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TradingCentralArticlesQuery}
   * @param {!TradingCentralArticlesQuery} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TradingCentralArticlesQuery)) {
      throw new TypeError('Instance of TradingCentralArticlesQuery is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TradingCentralArticlesQuery}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Timeframe: ((this.Timeframe != null) ? this.Timeframe : null),
      Limit: ((this.Limit != null) ? this.Limit : null),
      Language: ((this.Language != null) ? this.Language : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TradingCentralArticlesQuery.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TradingCentralArticlesQuery} other Object value
   * @returns {!TradingCentralArticlesQuery} Created struct
   */
  static fromObject (other) {
    return new TradingCentralArticlesQuery().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TradingCentralArticlesQuery}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TradingCentralArticlesQuery.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TradingCentralArticlesQuery}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5209
  }
}

export { TradingCentralArticlesQuery };

/**
 * Fast Binary Encoding TradingCentralArticlesQuery field model
 */
class FieldModelTradingCentralArticlesQuery extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Timeframe = new FieldModelTradingCentralTimeFrame(buffer, 4 + 4)
    this.Limit = new fbe.FieldModelUInt32(buffer, this.Timeframe.fbeOffset + this.Timeframe.fbeSize)
    this.Language = new FieldModelTradingCentralLanguage(buffer, this.Limit.fbeOffset + this.Limit.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelTradingCentralArticlesQuery}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTradingCentralArticlesQuery}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Timeframe.fbeSize + this.Limit.fbeSize + this.Language.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTradingCentralArticlesQuery}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Timeframe.fbeExtra + this.Limit.fbeExtra + this.Language.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTradingCentralArticlesQuery}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTradingCentralArticlesQuery.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTradingCentralArticlesQuery}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5209
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTradingCentralArticlesQuery}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTradingCentralArticlesQuery}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Timeframe.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Timeframe.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Timeframe.fbeSize

    if ((fbeCurrentSize + this.Limit.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Limit.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Limit.fbeSize

    if ((fbeCurrentSize + this.Language.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Language.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Language.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTradingCentralArticlesQuery}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTradingCentralArticlesQuery}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTradingCentralArticlesQuery}
   * @param {!TradingCentralArticlesQuery} fbeValue Default value, defaults is new TradingCentralArticlesQuery()
   * @returns {!TradingCentralArticlesQuery} TradingCentralArticlesQuery value
   */
  get (fbeValue = new TradingCentralArticlesQuery()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTradingCentralArticlesQuery}
   * @param {!TradingCentralArticlesQuery} fbeValue TradingCentralArticlesQuery value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Timeframe.fbeSize) <= fbeStructSize) {
      fbeValue.Timeframe = this.Timeframe.get(new TradingCentralTimeFrame(TradingCentralTimeFrame.DAY_7))
    } else {
      fbeValue.Timeframe = new TradingCentralTimeFrame(TradingCentralTimeFrame.DAY_7)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Timeframe.fbeSize

    if ((fbeCurrentSize + this.Limit.fbeSize) <= fbeStructSize) {
      fbeValue.Limit = this.Limit.get(100)
    } else {
      fbeValue.Limit = 100
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Limit.fbeSize

    if ((fbeCurrentSize + this.Language.fbeSize) <= fbeStructSize) {
      fbeValue.Language = this.Language.get(new TradingCentralLanguage(TradingCentralLanguage.EN))
    } else {
      fbeValue.Language = new TradingCentralLanguage(TradingCentralLanguage.EN)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Language.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTradingCentralArticlesQuery}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTradingCentralArticlesQuery}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTradingCentralArticlesQuery}
   * @param {!TradingCentralArticlesQuery} fbeValue TradingCentralArticlesQuery value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTradingCentralArticlesQuery}
   * @param {TradingCentralArticlesQuery} fbeValue TradingCentralArticlesQuery value
   */
  setFields (fbeValue) {
    this.Timeframe.set(fbeValue.Timeframe)
    this.Limit.set(fbeValue.Limit)
    this.Language.set(fbeValue.Language)
  }
}

export { FieldModelTradingCentralArticlesQuery };

/**
 * Fast Binary Encoding TradingCentralArticlesQuery model
 */
class TradingCentralArticlesQueryModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTradingCentralArticlesQuery(this.buffer, 4)
  }

  /**
   * Get the TradingCentralArticlesQuery model
   * @this {!TradingCentralArticlesQueryModel}
   * @returns {!FieldModelTradingCentralArticlesQuery} model TradingCentralArticlesQuery model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TradingCentralArticlesQueryModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TradingCentralArticlesQueryModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TradingCentralArticlesQueryModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TradingCentralArticlesQueryModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTradingCentralArticlesQuery.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TradingCentralArticlesQueryModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TradingCentralArticlesQueryModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TradingCentralArticlesQueryModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TradingCentralArticlesQueryModel}
   * @param {!TradingCentralArticlesQuery} value TradingCentralArticlesQuery value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TradingCentralArticlesQueryModel}
   * @param {!TradingCentralArticlesQuery} value TradingCentralArticlesQuery value, defaults is new TradingCentralArticlesQuery()
   * @return {!object} Deserialized TradingCentralArticlesQuery value and its size
   */
  deserialize (value = new TradingCentralArticlesQuery()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TradingCentralArticlesQuery(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TradingCentralArticlesQuery(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TradingCentralArticlesQueryModel}
   * @param {!number} prev Previous TradingCentralArticlesQuery model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TradingCentralArticlesQueryModel };
